interface LoadingSectionProps {
  readonly title: string;
  /** Announced to assistive technology. Never drawn — the skeleton is what the eye gets. */
  readonly message: string;
  /** Placeholders for controls that sit on the heading row, as the claims toolbar's do. */
  readonly headerExtra?: React.ReactNode;
  readonly children: React.ReactNode;
}

/**
 * Chrome for a section that is still loading: the real heading, the announcement, and the skeleton
 * standing in for the content.
 *
 * The announcement is the whole reason this exists rather than the skeletons being dropped in loose.
 * `SectionMessage` used to carry it in visible text; a skeleton alone is silence to a screen reader,
 * so the text moves to `sr-only` instead of disappearing.
 *
 * The chrome deliberately reproduces the loaded sections down to the class list — the heading row
 * matches `ClaimsToolbar`, and with no `headerExtra` it collapses to what `PolicyholderSection`
 * draws — so swapping between loading, loaded and `SectionMessage` never shifts the layout.
 */
export function LoadingSection({
  title,
  message,
  headerExtra,
  children,
}: LoadingSectionProps): React.JSX.Element {
  return (
    <section aria-busy="true" className="border-line rounded-xl border bg-surface p-6 shadow-sm">
      <div className="border-line flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <h2 className="text-brand-600 text-lg font-bold">{title}</h2>
        {headerExtra}
      </div>

      {/* `motion-safe:sr-only` hides the text only while the skeleton is allowed to animate. Under
          `prefers-reduced-motion` the placeholders are motionless grey blocks, which on their own say
          nothing to a sighted user who is not using a screen reader — so there, the words come back. */}
      <p role="status" className="text-muted mt-3 text-sm motion-safe:sr-only">
        {message}
      </p>

      <div className="mt-4">{children}</div>
    </section>
  );
}
